<template>
  <div class="post-media-slideshow">
    <div class="post-media-slideshow--chevron">
      <FriedRoundedButton
        v-if="postMedia.length > 1"
        compact
        class="carousel-button prev"
        :disabled="mediaIndex === 0"
        data-external="media-item-carousel-prev"
        @click="slide(-1)"
      >
        <template #icon>
          <FriedIcon icon="chevron-left" />
        </template>
      </FriedRoundedButton>
    </div>
    <div class="post-media-slideshow--content">
      <FriedSpinner v-if="isLoading" />
      <PostMediaItem
        v-else
        :media="postMedia[mediaIndex]"
        controls
        click-to-play
        autoplay
        :cover-fit="false"
        @media-load-error="$emit('media-load-error')"
      />
      <div v-if="postMedia.length > 1" class="circles">
        <span
          v-for="(media, index) in postMedia"
          :key="media.url"
          :class="['circle', { active: index === mediaIndex }]"
        ></span>
      </div>
    </div>
    <div class="post-media-slideshow--chevron">
      <FriedRoundedButton
        v-if="postMedia.length > 1"
        compact
        class="carousel-button next"
        data-external="media-item-carousel-next"
        :disabled="mediaIndex === postMedia.length - 1"
        @click="slide(1)"
      >
        <template #icon>
          <FriedIcon icon="chevron-right" />
        </template>
      </FriedRoundedButton>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import type { PostMedia } from '../../types/post';
import { PostMediaType } from '../../types/enums/post-media-type';
import PostMediaItem from './PostMediaItem.vue';

export default defineComponent({
  components: {
    PostMediaItem,
  },
  props: {
    postMedia: {
      type: Array as PropType<PostMedia[]>,
      required: true,
    },
    selectedMediaIndex: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const mediaIndex = ref(props.selectedMediaIndex);
    const isLoading = ref(false);

    const slide = async (direction: number) => {
      isLoading.value = true;
      mediaIndex.value += direction;
      await nextTick();
      isLoading.value = false;
    };

    const handleKeydown = event => {
      if (event.key === 'ArrowLeft' && mediaIndex.value > 0) {
        slide(-1);
      } else if (event.key === 'ArrowRight' && mediaIndex.value < props.postMedia.length - 1) {
        slide(1);
      }
    };

    onMounted(() => {
      document.addEventListener('keydown', handleKeydown);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', handleKeydown);
    });

    return {
      mediaIndex,
      slide,
      PostMediaType,
      isLoading,

      handleKeydown,
    };
  },
});
</script>
<style lang="scss" scoped>
.post-media-slideshow {
  display: flex;
  margin-top: var(--spacing-100);

  &--content {
    height: 100%;
    width: 100%;

    .circles {
      margin-top: var(--spacing-100);
      display: flex;
      gap: var(--spacing-25);
      justify-content: center;

      .circle {
        width: 0.5rem;
        height: 0.5rem;
        border-radius: var(--radius-medium);
        background-color: var(--gray-92);

        &.active {
          background-color: var(--gray-39);
        }
      }
    }
  }

  &--chevron {
    &:first-of-type {
      margin: auto var(--spacing-75) auto 0;
    }

    &:last-of-type {
      margin: auto 0 auto var(--spacing-75);
    }
  }

  .media-item {
    max-height: unset;
  }
}
</style>
